import React, { useState, useContext } from 'react';
import { UserContext } from '../../../lib/usercontext';
import PropTypes from 'prop-types';
import axios from 'axios';
import { getHeaders } from '../../../lib/requests';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import GeneralButton from '../button_general';
import { XMarkIcon } from '@heroicons/react/24/outline';
import ClientDropdown from '../dropdowns/dropdown_clients';
import MenuMultiSelect from '../multiselect/multiselect_menu';
import { handlePdfFile, extractErrorMessage } from '../../../lib/utils';
import { useNavigate } from 'react-router-dom';

const ReportModal = ({
  isOpen,
  onClose,
  title,
  description,
  endpoint,
  filenamePrefix,
  disabledCondition,
  downloadOption,
  filtersConfig,
  dateMode,
  maxDateRange
}) => {  
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const role = user.userGroups ? user.userGroups[0] : 'Customer';
  const isAdmin = role === 'Admin';

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [deliveryStatus, setDeliveryStatus] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedMenus, setSelectedMenus] = useState([]);
  const [loading, setLoading] = useState(false);

  const handlePrint = async () => {
    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const apiToken = localStorage.getItem('accessToken');
      let response;
      let maxRetries = 3;
      let retryCount = 0;

      const payload = {
        start_date: filtersConfig?.date !== false && startDate ? startDate.toISOString().split('T')[0] : null,
        end_date: filtersConfig?.date !== false && endDate ? endDate.toISOString().split('T')[0] : null,
        delivery_status: filtersConfig?.deliveryStatus !== false ? deliveryStatus : null,
        provider_id: filtersConfig?.client !== false ? selectedProvider?.client_id : null,
        menu_ids: filtersConfig?.menu !== false ? (selectedMenus.length > 0 ? selectedMenus.map(m => m.menu_id) : null) : null
      };

      while (retryCount <= maxRetries) {
        try {
          response = await axios.post(
            `${apiUrl}${endpoint}`,
            payload,
            { headers: getHeaders(apiToken), responseType: 'blob' }
          );
          break;
        } catch (error) {
          if (error.response?.status === 503 && retryCount < maxRetries) {
            retryCount++;
            const delay = Math.min(1000 * (2 ** retryCount) + Math.random() * 100, 10000);
            await new Promise(resolve => setTimeout(resolve, delay));
          } else {
            throw error;
          }
        }
      }

      const contentDisposition = response.headers['content-disposition'];
      let fileName = filenamePrefix;
      if (filtersConfig?.date !== false && startDate && endDate) {
        fileName += `_${startDate.toISOString().split('T')[0]}_${endDate.toISOString().split('T')[0]}.pdf`;
      } else {
        fileName += `.pdf`;
      }
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (fileNameMatch?.[1]) {
          fileName = fileNameMatch[1];
        }
      }

      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      handlePdfFile(pdfBlob, fileName, downloadOption);
    } catch (error) {
      console.error('Error generating PDF:', error);
      if (
        (error.response && [401, 403].includes(error.response.status)) ||
        !localStorage.getItem('accessToken')
      ) {
        onClose?.();
        navigate('/login?sessionExpired=true', { replace: true });
        return;
      }
      const message = await extractErrorMessage(error);
      alert(`Failed to generate PDF: ${message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const isDateValid =
    filtersConfig?.date === false ||
    (dateMode === 'range' ? (startDate && endDate) : startDate);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white w-full max-w-lg rounded shadow-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-semibold">{title}</h2>
            {description && <p className="text-sm text-gray-600 mt-1">{description}</p>}
          </div>
          <button onClick={onClose} className="hover:bg-gray-100 p-1 rounded">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          {filtersConfig?.date !== false &&
            (dateMode === 'range' ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Start Date</label>
                  <DatePicker
                    selected={startDate}
                    onChange={setStartDate}
                    selectsStart
                    startDate={startDate}
                    endDate={endDate}
                    dateFormat="yyyy-MM-dd"
                    className="border rounded w-full p-2 text-sm"
                    placeholderText="Select start date"
                    isClearable
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">End Date</label>
                  <DatePicker
                    selected={endDate}
                    onChange={setEndDate}
                    selectsEnd
                    startDate={startDate}
                    endDate={endDate}
                    minDate={startDate}
                    maxDate={
                      startDate && maxDateRange
                        ? new Date(startDate.getTime() + maxDateRange * 24 * 60 * 60 * 1000)
                        : undefined
                    }
                    dateFormat="yyyy-MM-dd"
                    className="border rounded w-full p-2 text-sm"
                    placeholderText="Select end date"
                    isClearable
                  />
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium mb-1">Date</label>
                <DatePicker
                  selected={startDate}
                  onChange={(date) => {
                    setStartDate(date);
                    setEndDate(date);
                  }}
                  dateFormat="yyyy-MM-dd"
                  className="border rounded w-full p-2 text-sm"
                  placeholderText="Select date"
                  isClearable
                />
              </div>
            ))}

          {filtersConfig?.deliveryStatus !== false && (
            <div>
              <label className="block text-sm font-medium mb-1">Delivery Status</label>
              <select
                value={deliveryStatus || ''}
                onChange={(e) => setDeliveryStatus(e.target.value)}
                className="border rounded w-full p-2 text-sm bg-white"
              >
                <option value="">Select</option>
                <option value="scheduled">Scheduled</option>
                <option value="delivered">Delivered</option>
                <option value="canceled">Canceled</option>
              </select>
            </div>
          )}

          {filtersConfig?.client !== false && isAdmin && (
            <div>
              <label className="block text-sm font-medium mb-1">Provider Filter</label>
              <div className="flex items-center gap-2">
                <ClientDropdown onSelect={setSelectedProvider} className="flex-1" />
                {selectedProvider && (
                  <button
                    onClick={() => setSelectedProvider(null)}
                    className="text-red-600 hover:text-red-800 text-sm whitespace-nowrap px-2 py-1 border rounded"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          )}

          {filtersConfig?.menu !== false && (
            <div>
              <div className="flex items-center gap-2">
                <MenuMultiSelect
                  label="Menu Filter"
                  onSelect={setSelectedMenus}
                  selectedMenus={selectedMenus}
                  className="flex-1"
                />
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <GeneralButton
            type="button"
            buttonType="secondary"
            onClick={onClose}
            className="px-4 py-2"
          >
            Cancel
          </GeneralButton>
          <GeneralButton
            type="button"
            buttonType="primary"
            onClick={handlePrint}
            disabled={disabledCondition || !isDateValid || loading}
            className="px-4 py-2"
          >
            {loading ? (
              <span className="flex items-center">
                <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                </svg>
                Generating...
              </span>
            ) : (
              "Generate Report"
            )}
          </GeneralButton>
        </div>
      </div>
    </div>
  );
};

ReportModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  endpoint: PropTypes.string.isRequired,
  filenamePrefix: PropTypes.string.isRequired,
  disabledCondition: PropTypes.bool,
  downloadOption: PropTypes.oneOf(['download', 'open', 'print']),
  filtersConfig: PropTypes.shape({
    date: PropTypes.bool,
    deliveryStatus: PropTypes.bool,
    client: PropTypes.bool,
    menu: PropTypes.bool
  }),
  dateMode: PropTypes.oneOf(['range', 'single']),
  maxDateRange: PropTypes.number
};

ReportModal.defaultProps = {
  downloadOption: 'download',
  filtersConfig: { date: true, deliveryStatus: true, client: true, menu: true },
  dateMode: 'range',
  maxDateRange: null
};

export default ReportModal;
